import React from 'react';
import {Link, useSearchParams} from 'react-router-dom';
import axiosClient from '../../services/axiosClient';
import './ModernJobsPost.css';
import { useCategories, STATIC_CATEGORIES } from '../../shared/hooks/useCategories.js';

// Utility to get auth header if logged in
function getAuthHeader() {
  try {
    const str = localStorage.getItem('userDetails');
    if (!str) return {};
    const { token, tokenType } = JSON.parse(str) || {};
    if (token) return { Authorization: `${tokenType || 'Bearer'} ${token}` };
    return {};
  } catch (_) {
    return {};
  }
}

// Safely lowercases any value (number, null, undefined, etc.)
const safeLower = (val) => {
  if (val === null || val === undefined) return '';
  try { return String(val).toLowerCase(); } catch (_) { return ''; }
};

function OfferRow({ offer }) {
  return (
    <div className="modern-job-card">
      <div className="job-card-header">
        <div className="job-avatar-container">
          <div className="job-avatar">
            {offer.logoUrl ? (
              <img src={offer.logoUrl} alt={offer.org || 'Logo'} className="job-logo" />
            ) : (
              <div className="job-initials">
                {(offer.org || '??').substring(0, 2).toUpperCase()}
              </div>
            )}
          </div>
          <div className="job-avatar-glow"></div>
        </div>
        
        <div className="job-main-info">
          <div className="job-title-section">
            <h3 className="job-title">
              <Link to={`/job-detail?id=${offer.id}`} className="job-link">{offer.title}</Link>
            </h3>
            <div className="job-company">
              <i className="fa-regular fa-building"></i>
              <span>{offer.org}</span>
            </div>
          </div>
          
          <div className="job-badges">
            <span className="job-badge category-badge">{offer.category}</span>
            <span className="job-badge type-badge">{offer.type}</span>
          </div>
        </div>
        
        <div className="job-actions">
          <button className="action-btn bookmark-btn" type="button">
            <i className="fa-regular fa-bookmark"></i>
          </button>
          <Link to={`/job-detail?id=${offer.id}`} className="action-btn primary-btn">
            <span>Voir l'offre</span>
            <i className="fa-solid fa-arrow-right"></i>
          </Link>
        </div>
      </div>
      
      <div className="job-meta">
        <div className="meta-item">
          <i className="fa-solid fa-location-dot"></i>
          <span>{offer.location}</span>
        </div>
        <div className="meta-item">
          <i className="fa-regular fa-calendar"></i>
          <span>{offer.date}</span>
        </div>
      </div>
      
      <div className="job-description">
        <p>{offer.excerpt}</p>
      </div>
      
      <div className="job-card-footer">
        <div className="job-tags">
          <span className="job-tag">#{safeLower(offer.category).replace(/\s+/g, '')}</span>
          <span className="job-tag">#{safeLower(offer.location).replace(/\s+/g, '')}</span>
        </div>
        <div className="job-time">
          <i className="fa-regular fa-clock"></i>
          <span>Il y a 2 jours</span>
        </div>
      </div>
      
      <div className="job-hover-effect"></div>
    </div>
  );
}

const JobsPost = () => {
  const usePagination = true; // enable pagination on /posts
  const [offers, setOffers] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');
  const [searchParams, setSearchParams] = useSearchParams();
  const initialPage = Number(searchParams.get('page') || 1);
  const initialPerPage = Number(searchParams.get('perPage') || 10);
  const [page, setPage] = React.useState(initialPage);
  const [perPage, setPerPage] = React.useState(initialPerPage);
  const [total, setTotal] = React.useState(0);
  const [lastPage, setLastPage] = React.useState(0);
  const lastFetchKeyRef = React.useRef('');
  // Post types from API
  const [postTypes, setPostTypes] = React.useState([]); // [{id, name}]
  const [postTypesLoading, setPostTypesLoading] = React.useState(false);
  // Dynamic categories for filter via shared cached hook (with static fallback)
  const { categories: catsFromHook } = useCategories();
  const categoryOptions = React.useMemo(() => {
    const arr = (catsFromHook || []).map(c => ({ id: c.id, name: c.name })).filter(c => !!c.name);
    return arr.length ? arr : STATIC_CATEGORIES.map((n, i) => ({ id: undefined, name: n }));
  }, [catsFromHook]);
  // Localities from API (fallback to a small static list)
  const [localities, setLocalities] = React.useState([]); // [{id,name}]
  const [localitiesLoading, setLocalitiesLoading] = React.useState(false);
  const STATIC_LOCATION_OPTIONS = [
    'Dakar','Pikine','Touba','Thiès','Thies Nones','Saint-Louis','Kaolack','Ziguinchor','Kolda','Matam','Louga'
  ];
  const LOCATION_ITEMS = React.useMemo(() => {
    if (localities && localities.length) return localities; // already {id,name}
    // fallback to static names (ids unknown yet)
    return STATIC_LOCATION_OPTIONS.map((n) => ({ id: null, name: n }));
  }, [localities, STATIC_LOCATION_OPTIONS]);

  // Search filters from URL
  const queryQ = (searchParams.get('q') || '').trim();
  const queryWhere = (searchParams.get('where') || '').trim();
  const queryCat = (searchParams.get('cat') || '').trim();
  // Backend-friendly ids if provided in URL
  const queryCategoryId = (searchParams.get('category_id') || '').trim();
  const queryCityId = (searchParams.get('city_id') || '').trim();
  const queryLocalityId = (searchParams.get('locality_id') || '').trim();
  const selectedTypes = searchParams.getAll('type'); // multiple
  const selectedCats = searchParams.getAll('cats'); // multiple
  const selectedLocs = searchParams.getAll('loc'); // multiple
  const published = (searchParams.get('published') || '').trim(); // 3d | 1w | 1m | 6m | 1y
  const queryPostTypeId = (searchParams.get('post_type_id') || '').trim();

  // Handlers to mutate URL params for filters
  const updateMultiParam = (key, value, checked) => {
    const next = new URLSearchParams(searchParams);
    const values = new Set(next.getAll(key));
    if (checked) values.add(value); else values.delete(value);
    next.delete(key);
    Array.from(values).forEach(v => next.append(key, v));
    // reset to page 1 when filters change
    next.set('page', '1');
    setSearchParams(next);
  };

  // Fetch localities from backend
  React.useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setLocalitiesLoading(true);
        const res = await axiosClient.get('/countries/SN/cities?perPage=100', {
          headers: { Accept: 'application/json' },
        });
        let data = res?.data;
        // Common Laravel shapes: {result:{data:[]}} or {data:[]}
        let list = data?.result?.data || data?.data || data || [];
        if (!Array.isArray(list) && Array.isArray(list?.data)) list = list.data;
        const mapped = list
          .map((c) => ({ id: c?.id ?? c?.city_id ?? c?.value, name: c?.name ?? c?.label }))
          .filter((c) => c.id && c.name);
        if (!mounted) return;
        setLocalities(mapped);
        // Update mapping with official ids
        const pairs = {};
        mapped.forEach((c) => { pairs[c.name] = c.id; });
        setDerivedLocalityMap((prev) => ({ ...prev, ...pairs }));
      } catch (e) {
        // keep fallback silently
        if (process.env.NODE_ENV !== 'production') console.warn('[JobsPost] cities fetch failed, using fallback');
      } finally {
        if (mounted) setLocalitiesLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  // Fetch post types from backend
  React.useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setPostTypesLoading(true);
        const res = await axiosClient.get('/postTypes', {
          headers: { Accept: 'application/json' },
        });
        let data = res?.data;
        // Common shapes: {result:{data:[]}} or {data:[]}
        let list = data?.result?.data || data?.data || data || [];
        if (!Array.isArray(list) && Array.isArray(list?.data)) list = list.data;
        const mapped = (list || [])
          .map((t, i) => ({ id: t?.id ?? t?.post_type_id ?? t?.value ?? i, name: t?.name ?? t?.title ?? t?.label }))
          .filter((t) => t.id && t.name);
        if (!mounted) return;
        setPostTypes(mapped);
      } catch (e) {
        if (process.env.NODE_ENV !== 'production') console.warn('[JobsPost] post types fetch failed');
      } finally {
        if (mounted) setPostTypesLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);
  const updateSingleParam = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value); else next.delete(key);
    next.set('page', '1');
    setSearchParams(next);
  };

  // Clear all filters and clean URL (no params)
  const clearAllFilters = () => {
    // Reset local pagination state
    setPage(1);
    setPerPage(10);
    // Remove all query params
    const empty = new URLSearchParams();
    setSearchParams(empty, { replace: true });
  };

  // Prefer IDs for category filter when available
  const setCategoryIdParam = (idOrEmpty) => {
    const next = new URLSearchParams(searchParams);
    // Remove legacy params to keep URL clean
    next.delete('cat');
    next.delete('cats');
    if (idOrEmpty) next.set('category_id', String(idOrEmpty)); else next.delete('category_id');
    next.set('page', '1');
    setSearchParams(next);
  };

  const [showAllLocations, setShowAllLocations] = React.useState(false);
  const [derivedLocalityMap, setDerivedLocalityMap] = React.useState({});
  // Static fallback mapping for common localities (used only if we don't have IDs from backend yet)
  const STATIC_LOCALITY_MAP = React.useMemo(() => ({
    'Dakar': 51,
    'Pikine': 0,
    'Touba': 0,
    'Thiès': 0,
    'Thies Nones': 0,
    'Saint-Louis': 0,
    'Kaolack': 0,
    'Ziguinchor': 0,
    'Kolda': 0,
    'Matam': 0,
    'Louga': 0,
  }), []);
  const LOCALITY_MAP = React.useMemo(() => ({...STATIC_LOCALITY_MAP, ...derivedLocalityMap}), [STATIC_LOCALITY_MAP, derivedLocalityMap]);

  const setLocalityIdParam = (idOrEmpty) => {
    const next = new URLSearchParams(searchParams);
    // remove legacy 'loc' and city_id for a clean URL
    next.delete('loc');
    // we prefer city_id in URL; also clear any previous locality_id/where for cleanliness
    next.delete('locality_id');
    if (idOrEmpty) {
      next.set('city_id', String(idOrEmpty));
      next.delete('where');
    } else {
      next.delete('city_id');
      next.delete('where');
    }
    next.set('page', '1');
    setSearchParams(next);
  };

  // Stable keys for deps
  const typesKey = React.useMemo(() => selectedTypes.slice().sort().join('|'), [selectedTypes]);
  const catsKey = React.useMemo(() => selectedCats.slice().sort().join('|'), [selectedCats]);
  const locsKey = React.useMemo(() => selectedLocs.slice().sort().join('|'), [selectedLocs]);

  const totalPages = React.useMemo(() => {
    // Compute from total/perPage and reconcile with API last_page
    const computed = perPage > 0 ? Math.max(1, Math.ceil(total / perPage)) : 1;
    const apiLast = Number(lastPage) > 0 ? Number(lastPage) : 0;
    return apiLast > 0 ? Math.max(apiLast, computed) : computed;
  }, [total, perPage, lastPage]);

  async function load(p = page, limit = perPage) {
    try {
      setLoading(true);
      const config = {
        headers: { Accept: 'application/json', ...getAuthHeader() },
      };
      
      // Build API params with correct format
      const params = {};
      
      // ALWAYS set op=search first for filter activation
      params.op = 'search';
      
      // Pagination - use camelCase perPage instead of per_page
      if (usePagination) {
        params.page = p;
        params.perPage = limit;
      }
      
      // Search query
      if (queryQ) params.q = queryQ;
      
      // Try to detect numeric ids for category and city
      const isNumericId = (v) => v !== undefined && v !== null && /^\d+$/.test(String(v).trim());
      const catCandidate = queryCategoryId || queryCat || (selectedCats[0] || '');
      const locCandidate = queryCityId || queryLocalityId || queryWhere || (selectedLocs[0] || '');
      const postTypeCandidate = queryPostTypeId || '';
      
      // Category - use 'c' instead of 'category_id'
      if (isNumericId(catCandidate)) {
        params.c = String(catCandidate).trim();
      }
      
      // Location - use 'l' instead of 'city_id'
      if (isNumericId(locCandidate)) {
        params.l = String(locCandidate).trim();
      }
      
      // Post type - keep post_type_id as is
      if (isNumericId(postTypeCandidate)) {
        params.post_type_id = String(postTypeCandidate).trim();
      }
      
      // Date filter - map to API format
      if (published) {
        const dateMapping = {
          '3d': 'last3days',
          '1w': 'last7days',
          '1m': 'last30days',
          '6m': 'last150days',
          '1y': 'last365days'
        };
        params.postedDate = dateMapping[published] || published;
      }
      
      config.params = params;
      
      console.log('[JobsPost] API Request:', {
        url: '/posts',
        params: config.params
      });
      
      const resp = await axiosClient.get('/posts', config);

      const data = resp?.data;
      // Try to read common Laravel pagination shapes
      const pagination = data?.result || data;
      const meta = pagination?.meta || {};
      // Some APIs nest the paginator under a `data` object alongside the list under `data.data`
      const inner = pagination?.data && !Array.isArray(pagination.data) ? pagination.data : null;
      let items = inner?.data || pagination?.data || data?.data || data?.result || [];
      if (!Array.isArray(items)) {
        if (items && Array.isArray(items.data)) items = items.data; else items = [];
      }

      // totals
      const metaTotal = (
        inner?.total ?? pagination?.total ?? data?.total ?? meta?.total
      );
      const metaPerPage = (
        inner?.per_page ?? pagination?.per_page ?? data?.per_page ?? meta?.per_page
      );
      const metaLastPage = (
        inner?.last_page ?? pagination?.last_page ?? data?.last_page ?? meta?.last_page
      );
      const metaCurrentPage = (
        inner?.current_page ?? pagination?.current_page ?? data?.current_page ?? meta?.current_page
      );

      let mapped = items.map((it) => {
        const rawCategory = it.category_name || it.category;
        const rawCity = it.city || it.location;
        const categoryStr = (rawCategory && typeof rawCategory === 'object') ? (rawCategory.name || rawCategory.title || rawCategory.label || '—') : (rawCategory || '—');
        const cityStr = (rawCity && typeof rawCity === 'object') ? (rawCity.name || rawCity.title || rawCity.label || '—') : (rawCity || '—');
        const categoryId = (it.category_id ?? it.categoryId ?? ((rawCategory && typeof rawCategory === 'object') ? rawCategory.id : null));
        const cityId = (it.city_id ?? it.cityId ?? ((rawCity && typeof rawCity === 'object') ? rawCity.id : null));
        return ({
          id: it.id || it.uuid || it.slug || Math.random().toString(36).slice(2),
          org: it.company_name || it.company || it.organisation || it.publisher || '—',
          title: it.title || it.name || '—',
          category: categoryStr,
          categoryId,
          location: cityStr,
          cityId,
          type: (typeof it.post_type === 'object' ? (it.post_type?.name || it.post_type?.title) : (it.type || it.post_type)) || '—',
          postTypeId: it.post_type_id ?? (typeof it.post_type === 'object' ? it.post_type?.id : null),
          date: it.created_at_formatted || it.created_at || it.date || '',
          excerpt: it.excerpt || it.description_excerpt || it.description || '',
          logoUrl: (() => {
            let logoUrl = '';
            if (it.logo_url) {
              logoUrl = it.logo_url.small || it.logo_url.medium || it.logo_url.full;
              // Fix localhost SSL issues
              if (logoUrl && logoUrl.includes('https://localhost:8000')) {
                logoUrl = logoUrl.replace('https://localhost:8000', 'http://localhost:8000');
              }
            } else if (it.logo) {
              logoUrl = `http://localhost:8000/storage/${it.logo}`;
            }
            return logoUrl;
          })(),
        });
      });

      // Client-side filtering fallback to match URL params immediately
      const qLower = safeLower(queryQ);
      const whereLower = safeLower(queryWhere);
      const catLower = safeLower(queryCat);
      const typeSet = new Set(selectedTypes.map(s => s.toLowerCase())); // legacy text-based filter
      const catSet = new Set(selectedCats.map(s => s.toLowerCase()));
      const locSet = new Set(selectedLocs.map(s => s.toLowerCase()));
      const wantedPostTypeId = queryPostTypeId ? String(queryPostTypeId) : '';
      const now = new Date();
      const maxAgeMs = (function(){
        switch(published){
          case '3d': return 3*24*60*60*1000;
          case '1w': return 7*24*60*60*1000;
          case '1m': return 30*24*60*60*1000;
          case '6m': return 180*24*60*60*1000;
          case '1y': return 365*24*60*60*1000;
          default: return 0;
        }
      })();
      if (qLower || whereLower || catLower || typeSet.size || catSet.size || locSet.size || maxAgeMs) {
        mapped = mapped.filter((o) => {
          const matchQ = qLower ? (
            safeLower(o.title).includes(qLower) ||
            safeLower(o.org).includes(qLower) ||
            safeLower(o.excerpt).includes(qLower)
          ) : true;
          const matchWhere = whereLower ? (
            safeLower(o.location).includes(whereLower)
          ) : true;
          const matchCat = catLower ? (
            safeLower(o.category).includes(catLower)
          ) : true;
          const matchTypeChips = typeSet.size ? typeSet.has(safeLower(o.type)) : true;
          const matchPostTypeId = wantedPostTypeId ? (String(o.postTypeId || '') === wantedPostTypeId) : true;
          const matchCatChips = catSet.size ? catSet.has(safeLower(o.category)) : true;
          const matchLocChips = locSet.size ? locSet.has(safeLower(o.location)) : true;
          let matchDate = true;
          if (maxAgeMs) {
            const d = o.date ? new Date(o.date) : null;
            if (d && !isNaN(d.getTime())) {
              matchDate = (now - d) <= maxAgeMs;
            }
          }
          return matchQ && matchWhere && matchCat && matchTypeChips && matchCatChips && matchLocChips && matchPostTypeId && matchDate;
        });
      }

      // Debug meta parsed from API
      console.debug('[JobsPost] pagination meta', {
        metaTotal,
        metaPerPage,
        metaLastPage,
        metaCurrentPage,
        requested: { p, limit },
        computedTotalPages: (Number(metaPerPage) > 0 && Number(metaTotal) > 0)
          ? Math.ceil(Number(metaTotal) / Number(metaPerPage))
          : null,
      });

      setOffers(mapped);
      // Derive locality ids from fetched items
      const nameIdPairs = {};
      mapped.forEach(o => {
        if (o.location && o.cityId) {
          nameIdPairs[o.location] = o.cityId;
        }
      });
      if (Object.keys(nameIdPairs).length) setDerivedLocalityMap(nameIdPairs);
      // Prefer filtered count when filters are active
      const filteredActive = Boolean(qLower || whereLower || catLower);
      const nextTotal = filteredActive ? mapped.length : (Number(metaTotal) || mapped.length);
      const nextPerPage = Number(limit) || Number(metaPerPage) || 10;
      const apiLast = filteredActive ? 0 : (Number(metaLastPage) || 0);
      const computedPages = nextPerPage > 0 ? Math.max(1, Math.ceil(nextTotal / nextPerPage)) : 1;
      setTotal(nextTotal);
      if (usePagination) {
        // keep perPage aligned with our requested limit (fallback to API meta)
        setPerPage(nextPerPage);
        // Trust the larger between API's last_page and the computed one
        setLastPage(Math.max(apiLast, computedPages));
        if (!filteredActive && Number(metaCurrentPage) && Number(metaCurrentPage) !== page) {
          setPage(Number(metaCurrentPage));
        }
        // If filters reduce total pages and current page is out of range, reset to 1
        if (filteredActive && page > computedPages) {
          setPage(1);
        }
      } else {
        // when no pagination, just show all received items
        setLastPage(1);
      }
      setError('');
    } catch (e) {
      console.error('[JobsPost] fetch error:', e);
      const msg = e?.response?.data?.message || e?.message || 'Erreur réseau';
      setError(`Impossible de charger les offres: ${msg}`);
    } finally {
      setLoading(false);
    }
  }

  // Effects
  React.useEffect(() => {
    if (!usePagination) {
      // single fetch without params, keep URL clean
      load();
      return;
    }
    // pagination mode default: 10 per page and clean URL on page 1
    const isDefault = page === 1 && perPage === 10;
    if (usePagination) {
      // Update URL (page, perPage) but preserve existing filters
      const next = new URLSearchParams(searchParams);
      next.set('page', String(page));
      next.set('perPage', String(perPage));
      setSearchParams(next, { replace: true });
    }
    const key = `${page}|${perPage}|${queryQ}|${queryWhere}|${queryCat}|${selectedTypes.join(',')}|${selectedCats.join(',')}|${selectedLocs.join(',')}|${published}|${queryPostTypeId}|${queryCategoryId}|${queryCityId}|${queryLocalityId}`;
    if (lastFetchKeyRef.current === key) return;
    lastFetchKeyRef.current = key;
    load(page, perPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, perPage, usePagination, queryQ, queryWhere, queryCat, published, typesKey, catsKey, locsKey, queryPostTypeId, queryCategoryId, queryCityId, queryLocalityId]);

  function goTo(newPage) {
    if (newPage < 1 || newPage > totalPages || newPage === page) return;
    setPage(newPage);
  }

  // Render page numbers around the current page
  const pageNumbers = React.useMemo(() => {
    const maxButtons = 5; // current +/- 2
    const pages = [];
    let start = Math.max(1, page - 2);
    let end = Math.min(totalPages, start + maxButtons - 1);
    start = Math.max(1, end - maxButtons + 1);
    for (let i = start; i <= end; i++) pages.push(i);
    return pages;
  }, [page, totalPages]);

  return (
    <div className="container">
      <div className="row">
        <div className="col-12 col-lg-9">
          {/* Modern Header */}
          <div className="modern-jobs-header">
          <div className="jobs-header-content">
            <div className="jobs-count-badge">
              <i className="fa-solid fa-briefcase"></i>
              <span>{total} offres disponibles</span>
            </div>
            <h2 className="jobs-section-title">
              Opportunités d'Appels d'offres
              <div className="title-decoration"></div>
            </h2>
            <p className="jobs-section-subtitle">
              Découvrez les meilleurs Appels d'offres correspondant à vos critères
            </p>
          </div>
          
          <div className="jobs-view-options">
            <div className="view-toggle">
              <button className="view-btn active" data-view="list">
                <i className="fa-solid fa-list"></i>
              </button>
              <button className="view-btn" data-view="grid">
                <i className="fa-solid fa-grid-2"></i>
              </button>
            </div>
            <div className="sort-dropdown">
              <select className="sort-select">
                <option value="recent">Plus récentes</option>
                <option value="relevant">Plus pertinentes</option>
                <option value="company">Par entreprise</option>
                <option value="location">Par localisation</option>
              </select>
            </div>
          </div>
        </div>

        {/* Content */}
        {loading && <div className="text-muted">Chargement…</div>}
        {error && !loading && <div className="alert alert-danger">{error}</div>}
        {!loading && !error && (
          <div>
            {offers.length === 0 && (
              <div className="text-muted">Aucune offre disponible.</div>
            )}
            <div className="modern-jobs-grid">
              {offers.map((o) => (
                <OfferRow key={o.id} offer={o} />
              ))}
            </div>

            {/* Pagination (visible when usePagination=true) */}
            {usePagination && (
            <nav className="d-flex justify-content-center mt-3 mt-md-4" aria-label="Pagination des offres">
              <ul className="pagination">
                <li className={`page-item ${page <= 1 ? 'disabled' : ''}`}>
                  <button className="page-link" onClick={() => goTo(1)} aria-label="Première">
                    ««
                  </button>
                </li>
                <li className={`page-item ${page <= 1 ? 'disabled' : ''}`}>
                  <button className="page-link" onClick={() => goTo(page - 1)} aria-label="Précédent">
                    «
                  </button>
                </li>
                {pageNumbers[0] > 1 && (
                  <li className="page-item">
                    <button className="page-link" onClick={() => goTo(1)}>1</button>
                  </li>
                )}
                {pageNumbers[0] > 2 && (
                  <li className="page-item disabled"><span className="page-link">…</span></li>
                )}
                {pageNumbers.map((n) => (
                  <li key={n} className={`page-item ${n === page ? 'active' : ''}`}>
                    <button className="page-link" onClick={() => goTo(n)}>{n}</button>
                  </li>
                ))}
                {pageNumbers[pageNumbers.length - 1] < totalPages - 1 && (
                  <li className="page-item disabled"><span className="page-link">…</span></li>
                )}
                {pageNumbers[pageNumbers.length - 1] < totalPages && (
                  <li className="page-item">
                    <button className="page-link" onClick={() => goTo(totalPages)}>{totalPages}</button>
                  </li>
                )}
                <li className={`page-item ${page >= totalPages ? 'disabled' : ''}`}>
                  <button className="page-link" onClick={() => goTo(page + 1)} aria-label="Suivant">
                    »
                  </button>
                </li>
                <li className={`page-item ${page >= totalPages ? 'disabled' : ''}`}>
                  <button className="page-link" onClick={() => goTo(totalPages)} aria-label="Dernière">
                    »»
                  </button>
                </li>
              </ul>
            </nav>
            )}
          </div>
        )}
      </div>

      {/* Modern Sidebar Filters */}
      <div className="d-none d-lg-block col-lg-3">
        <div className="modern-filters-sidebar">
          <div className="filters-header">
            <h3 className="filters-title">
              <i className="fa-solid fa-filter"></i>
              Filtres
            </h3>
            <button className="clear-filters-btn" onClick={clearAllFilters}>
              <i className="fa-solid fa-rotate-left"></i>
              Effacer
            </button>
          </div>
          
          <div className="filter-section">
            <h4 className="filter-section-title">Type d'offre</h4>
            <div className="filter-chips">
              {postTypesLoading && (
                <span className="text-muted" style={{fontSize:'0.9rem'}}>Chargement…</span>
              )}
              {!postTypesLoading && postTypes.map(pt => {
                const active = String(queryPostTypeId || '') === String(pt.id);
                return (
                  <button
                    key={pt.id}
                    type="button"
                    className={`filter-chip ${active ? 'active' : ''}`}
                    onClick={() => updateSingleParam('post_type_id', active ? '' : String(pt.id))}
                  >
                    {pt.name}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="filter-section">
            <h4 className="filter-section-title">Catégories</h4>
            <div className="filter-checkboxes">
              {categoryOptions.map(c => {
                const checked = selectedCats.includes(c.name)
                  || (queryCategoryId && String(c.id ?? '') === String(queryCategoryId));
                const hasId = c.id !== undefined && c.id !== null && String(c.id) !== '';
                return (
                  <label key={c.id ?? c.name} className={`filter-checkbox ${checked ? 'checked' : ''}`}>
                    <input 
                      type="checkbox" 
                      checked={checked} 
                      onChange={(e)=>{
                        if (hasId) {
                          // Use single category_id param; toggle current id
                          const willCheck = e.target.checked;
                          setCategoryIdParam(willCheck ? c.id : '');
                        } else {
                          // Fallback to legacy multi-name param
                          updateMultiParam('cats', c.name, e.target.checked);
                        }
                      }} 
                    />
                    <div className="checkbox-custom">
                      <i className="fa-solid fa-check"></i>
                    </div>
                    <span className="checkbox-label">{c.name}</span>
                  </label>
                );
              })}
            </div>
          </div>

          <div className="filter-section">
            <h4 className="filter-section-title">Localités</h4>
            <div className="filter-checkboxes">
              {/* Option to clear selection */}
              <label className={`filter-radio ${!queryCityId ? 'checked' : ''}`}>
                <input
                  type="radio"
                  name="city_filter"
                  checked={!queryCityId}
                  onChange={()=> setLocalityIdParam('')}
                />
                <div className="radio-custom"></div>
                <span className="radio-label">Toutes</span>
              </label>
              {(showAllLocations ? LOCATION_ITEMS : LOCATION_ITEMS.slice(0,8)).map(item => {
                const l = item.name;
                const id = item.id;
                const checked = Boolean(queryCityId) && String(id || LOCALITY_MAP[l] || '') === String(queryCityId);
                const disabled = localitiesLoading || !id;
                return (
                  <label key={l} className={`filter-radio ${checked ? 'checked' : ''} ${disabled ? 'disabled' : ''}`} title={disabled ? 'Chargement des villes ou ID indisponible' : ''}>
                    <input 
                      type="radio"
                      name="city_filter"
                      checked={checked} 
                      disabled={disabled}
                      onChange={()=>{
                        const finalId = id || LOCALITY_MAP[l];
                        // Only use city_id; if id unknown, do nothing
                        setLocalityIdParam(finalId || '');
                      }} 
                    />
                    <div className="radio-custom"></div>
                    <span className="radio-label">{l}</span>
                  </label>
                );
              })}
            </div>
            {LOCATION_ITEMS.length > 8 && (
              <button className="show-more-btn" onClick={()=>setShowAllLocations(v=>!v)}>
                {showAllLocations ? 'Voir moins' : `Voir plus (${LOCATION_ITEMS.length-8})`}
                <i className={`fa-solid fa-chevron-${showAllLocations ? 'up' : 'down'}`}></i>
              </button>
            )}
          </div>

          <div className="filter-section">
            <h4 className="filter-section-title">Date de publication</h4>
            <div className="filter-radios">
              {[
                {v:'3d', label:"il y a 3 jours"},
                {v:'1w', label:"il y a 1 semaine"},
                {v:'1m', label:"il y a 1 mois"},
                {v:'6m', label:"il y a 6 mois"},
                {v:'1y', label:"il y a 1 an"},
              ].map(opt => (
                <label key={opt.v} className={`filter-radio ${published===opt.v ? 'checked' : ''}`}>
                  <input 
                    type="radio" 
                    name="published" 
                    checked={published===opt.v} 
                    onChange={()=>updateSingleParam('published', opt.v)} 
                  />
                  <div className="radio-custom"></div>
                  <span className="radio-label">{opt.label}</span>
                </label>
              ))}
              <label className={`filter-radio ${!published ? 'checked' : ''}`}>
                <input 
                  type="radio" 
                  name="published" 
                  checked={!published} 
                  onChange={()=>updateSingleParam('published', '')} 
                />
                <div className="radio-custom"></div>
                <span className="radio-label">Toutes</span>
              </label>
            </div>
          </div>
        </div>
      </div>
    </div>

    {/* Bottom CTA section */}
    <div className="mt-4 mt-lg-5">
      <div className="card border-0 rounded-4 shadow-sm" style={{background: '#f8f9fa'}}>
        <div className="card-body py-4 py-md-5">
          <div className="text-center">
            <h4 className="mb-2">Recherchez-vous un travail?</h4>
            <p className="text-muted mb-3 mb-md-4">Envoyez votre CV et postulez facilement aux Appels d'offres depuis n'importe quel support!</p>
            <a href="#" className="btn btn-warning fw-semibold">
              <i className="fa-regular fa-file me-2"></i>
              Ajouter votre CV
            </a>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
};

export default JobsPost;